import { Card } from "react-bootstrap";
import NewDynamicDate from "./FormComponents/NewDynamicDate";
import NewDynamicText from "./FormComponents/NewDynamicText";
import NewDynamicSelect from "./FormComponents/NewDynmaicSelect";
import { useEffect, useState } from "react";
import Icon from "../generic/Icon";
import { truncateText, formatNumber } from "./utils";
import { useSiteContext } from "./Providers/SSCProvider";
import DiverseIcon from "./DiverseIcon";
import { useStructureContext } from "./Providers/StructureProvider";
import { useRoleContext } from "./Providers/RoleProvider";
import NotesModal from "./Modals/NotesModal";

const SingleCircuit = ({
  initialCircuit,
  circuitFields,
  invalidFields,
  setShowDiverseModal,
  setCicruitModalData,
}) => {
  const { isAdmin } = useRoleContext();
  const [updatedCircuit, setUpdatedCircuit] = useState(initialCircuit);
  const [notesModal, setNotesModal] = useState({ show: false });

  const { handleCircuitChange, selectedSite, removeTempCircuit } =
    useSiteContext();
  const { highlightData } = useStructureContext();

  useEffect(() => {
    setUpdatedCircuit(initialCircuit);
  }, [initialCircuit]);

  const valueChangeHandler = ({ key, value }) => {
    const newCircuit = { ...updatedCircuit, [key]: value };
    setUpdatedCircuit(newCircuit);

    const diff = {};
    Object.keys(newCircuit).forEach((k) => {
      if (newCircuit[k] !== initialCircuit[k]) diff[k] = newCircuit[k];
    });

    handleCircuitChange(
      initialCircuit.structureId,
      initialCircuit.circuitGmId,
      diff,
    );
  };

  const circuitGroups =
    selectedSite?.diversityGroups?.filter((group) =>
      updatedCircuit.diversityGroupIds?.includes(group.id),
    ) || [];

  const isHighlighted =
    highlightData &&
    initialCircuit.diversityGroupIds?.includes(highlightData.groupId);

  const renderField = (field, isInlineGroupItem = false) => {
    const isUpdatedValue =
      updatedCircuit[field.key] !== initialCircuit[field.key];
    const inputWidth = isInlineGroupItem ? 100 : field.width;

    // Diversity field
    if (field.type === "diversity") {
      return (
        <div
          key={field.key}
          className="flex gap-2 overflow-x-auto whitespace-nowrap p-1"
          style={{ scrollbarWidth: "thin", minHeight: "40px" }}
        >
          {circuitGroups.map((group) => (
            <DiverseIcon
              key={group.id}
              group={group}
              count={group.circuitCount}
              variant={group.diversity}
              disabled={false}
              setShowDiverseModal={setShowDiverseModal}
              value={group.diversityStatus?.id}
            />
          ))}
        </div>
      );
    }

    if (field.type === "select") {
      if (isInlineGroupItem) {
        return (
          <NewDynamicSelect
            valueKey={field.key}
            value={updatedCircuit[field.key] ?? ""}
            options={field.options}
            getOptionLabel={field.getOptionLabel}
            getOptionValue={field.getOptionValue}
            placeholder={field.placeholder}
            isUpdated={isUpdatedValue}
            isRequired={invalidFields.includes(field.key)}
            onChange={valueChangeHandler}
            inputWidth={inputWidth}
          />
        );
      }

      return (
        <div
          key={field.key}
          style={{ display: "flex", alignItems: "center", gap: 4 }}
        >
          <NewDynamicSelect
            valueKey={field.key}
            value={updatedCircuit[field.key] ?? ""}
            options={field.options}
            getOptionLabel={field.getOptionLabel}
            getOptionValue={field.getOptionValue}
            placeholder={field.placeholder}
            isUpdated={isUpdatedValue}
            isRequired={invalidFields.includes(field.key)}
            onChange={valueChangeHandler}
            inputWidth={inputWidth}
          />

          {(field.key === "carrier" || field.key === "lec") && (
            <InfoIcon
              carrierName={
                field.key === "lec"
                  ? updatedCircuit.lec
                  : updatedCircuit.carrier
              }
            />
          )}
        </div>
      );
    }

    if (field.type === "date") {
      return (
        <NewDynamicDate
          key={field.key}
          valueKey={field.key}
          value={updatedCircuit[field.key] || ""}
          isUpdated={isUpdatedValue}
          onChange={valueChangeHandler}
          inputWidth={inputWidth}
        />
      );
    }

    if (isInlineGroupItem) {
      return (
        <NewDynamicText
          valueKey={field.key}
          value={
            field.key === "mrcBaseline"
              ? formatNumber(updatedCircuit[field.key])
              : updatedCircuit[field.key] || ""
          }
          isUpdated={isUpdatedValue}
          isRequired={invalidFields.includes(field.key)}
          onChange={valueChangeHandler}
          inputWidth={inputWidth}
        />
      );
    }

    return (
      <div
        key={field.key}
        style={{ display: "flex", alignItems: "center", gap: 4 }}
      >
        <NewDynamicText
          valueKey={field.key}
          value={
            field.key === "mrcBaseline"
              ? formatNumber(updatedCircuit[field.key])
              : updatedCircuit[field.key] || ""
          }
          isUpdated={isUpdatedValue}
          isRequired={invalidFields.includes(field.key)}
          onChange={valueChangeHandler}
          inputWidth={inputWidth}
        />

        {field.key === "circuitVendorId" && (
          <CircuitIcons
            isAdmin={isAdmin}
            updatedCircuit={updatedCircuit}
            initialCircuit={initialCircuit}
            setNotesModal={setNotesModal}
            setCicruitModalData={setCicruitModalData}
            removeTempCircuit={removeTempCircuit}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Card
        className="ps-2  pt-2 pe-0 pb-2"
        style={{
          border: "2px solid",
          borderColor: isHighlighted ? highlightData?.color : "transparent",
          transition: "border-color 0.6s ease",
          backgroundColor:
            initialCircuit.status === "Decom" ? "rgb(236, 236, 236)" : "white",
          width: "225px",
          flexShrink: 0,
        }}
      >
        <Card.Body className="ps-0 pe-1 pb-0">
          {circuitFields.map((field, idx) => {
            const next = circuitFields[idx + 1];
            const sectionEnds = !next || next.section !== field.section;

            if (field.inlineGroup) {
              const inlineFields = circuitFields.filter(
                (f) => f.inlineGroup === field.inlineGroup,
              );

              if (field.key !== inlineFields[0].key) return null;

              const groupLastKey = inlineFields[inlineFields.length - 1].key;
              const groupLastIndex = circuitFields.findIndex(
                (f) => f.key === groupLastKey,
              );
              const afterGroup = circuitFields[groupLastIndex + 1];
              const groupSectionEnds =
                !afterGroup || afterGroup.section !== field.section;

              if (field.inlineGroup === "statusUse") {
                const statusField = inlineFields.find(
                  (f) => f.key === "status",
                );
                const useField = inlineFields.find((f) => f.key === "use");

                if (statusField && useField) {
                  return (
                    <div
                      key={field.inlineGroup}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "nowrap",
                        width: "100%",
                        gap: 4,
                        marginBottom: groupSectionEnds ? "1.5rem" : 0,
                      }}
                    >
                      <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        {renderField(statusField, true)}
                      </div>
                      <span style={{ flex: "0 0 auto", marginTop: "4px" }}>
                        /
                      </span>
                      <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        {renderField(useField, true)}
                      </div>
                    </div>
                  );
                }
              }

              return (
                <div
                  key={field.inlineGroup}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    width: "100%",
                    gap: 4,
                    marginBottom: groupSectionEnds ? "1.5rem" : 0,
                  }}
                >
                  {inlineFields.map((f, i) => (
                    <div
                      key={f.key}
                      style={{
                        flex: f.width ? `0 0 ${f.width}%` : "1 1 auto",
                        minWidth: 0,
                      }}
                    >
                      {renderField(f, true)}
                    </div>
                  ))}
                </div>
              );
            }

            // Diversity
            if (field.type === "diversity") {
              return (
                <div
                  key={field.key}
                  style={{ marginBottom: sectionEnds ? "1.5rem" : 0 }}
                >
                  {renderField(field)}
                </div>
              );
            }

            return (
              <div
                key={field.key}
                style={{ marginBottom: sectionEnds ? "1.5rem" : 0 }}
              >
                {renderField(field)}
              </div>
            );
          })}
        </Card.Body>
        <NotesModal
          notesData={notesModal}
          setNotesModal={setNotesModal}
          setUpdatedCircuit={setUpdatedCircuit}
        />
      </Card>
    </>
  );
};

const CircuitIcons = ({
  isAdmin,
  updatedCircuit,
  initialCircuit,
  setNotesModal,
  setCicruitModalData,
  removeTempCircuit,
}) => {
  const hasNote = !!updatedCircuit.circuitNote;

  return (
    <>
      <Icon
        name="delete"
        onClick={() => {
          if (initialCircuit.isNew) {
            removeTempCircuit(
              initialCircuit.structureId,
              initialCircuit.circuitGmId,
            );
          } else {
            setCicruitModalData({ show: true, circuit: initialCircuit });
          }
        }}
        overrides={{
          disabled: !isAdmin,
          tooltip: "Delete Circuit",
          tooltipPlacement: "top",
          size: "1rem",
          style: { marginLeft: "5px" },
        }}
      />
      <Icon
        name="note"
        onClick={() =>
          setNotesModal({
            show: true,
            circuitNote: updatedCircuit.circuitNote,
            circuitId: updatedCircuit.circuitGmId,
            structureId: updatedCircuit.structureId,
          })
        }
        overrides={{
          tooltip: hasNote
            ? truncateText(
                updatedCircuit.circuitNote.replace(/\\n/g, "\n"),
                200,
              )
            : "No notes available",
          tooltipPlacement: "auto",
          tooltipClass: "pre-wrap-tooltip",
          iconColor: hasNote ? "#CA8A04" : "#808080",
          style: { marginLeft: "10px", cursor: "pointer" },
        }}
      />
    </>
  );
};

const InfoIcon = ({ carrierName }) => {
  const { handleCarrierInfoClick } = useSiteContext();
  return (
    <Icon
      name="info"
      onClick={() => handleCarrierInfoClick(carrierName || "")}
      overrides={{
        tooltip: "Carrier Info",
        tooltipPlacement: "auto",
        size: "1rem",
        style: { marginLeft: "10px" },
      }}
    />
  );
};

export default SingleCircuit;
